export class ArrayIterator {
    constructor(array) {
        this.array = array;
        this.count = 0;
    }
    next() {
        do {
            const v = this.array[this.count++];
            const done = this.count >= this.array.length;
            if (v || done) {
                return {
                    done,
                    value: v,
                };
            }
        } while (this.count < this.array.length);
        return {
            done: true,
            value: undefined,
        };
    }
}
export class ArrayMap {
    constructor(getKey, array) {
        this.getKey = getKey;
        this.array = array || [];
        this.primaryIndexMap = {};
        this.deleted = 0;
        this.array.forEach((it, index) => {
            const key = this.getKey(it);
            if (key)
                this.primaryIndexMap[key] = index;
        });
    }
    [Symbol.iterator]() {
        return new ArrayIterator(this.array);
    }
    getByIndex(idx) {
        return this.indexAcceptable(idx) ? this.array[idx] : undefined;
    }
    indexAcceptable(idx) {
        return idx !== undefined && idx >= 0 && idx < this.array.length;
    }
    getByKey(key, indexName = 'primary') {
        if (indexName === 'primary') {
            if (key && key in this.primaryIndexMap) {
                const idx = this.primaryIndexMap[key];
                return this.getByIndex(idx);
            }
        }
        return undefined;
    }
    get(item) {
        const key = this.getKey(item);
        return key ? this.getByKey(key) : undefined;
    }
    add(item, replace) {
        if (!item)
            return false;
        const key = this.getKey(item);
        if (!key)
            return false;
        const exist = key in this.primaryIndexMap && this.indexAcceptable(this.primaryIndexMap[key]);
        if (exist && !replace)
            return false;
        if (!exist) {
            const len = this.array.push(item);
            this.primaryIndexMap[key] = len - 1;
            return true;
        }
        if (exist && replace) {
            const idx = this.primaryIndexMap[key];
            this.array[idx] = item;
            return true;
        }
        return false;
    }
    addAll(items, replace) {
        if (!items || items.length === 0)
            return 0;
        let ret = 0;
        items.forEach(it => {
            if (this.add(it, replace))
                ret++;
        });
        return ret;
    }
    remove(item) {
        if (!item)
            return false;
        const key = this.getKey(item);
        if (!key)
            return false;
        if (!(key in this.primaryIndexMap))
            return false;
        const idx = this.primaryIndexMap[key];
        if (this.indexAcceptable(idx)) {
            delete this.array[idx];
            delete this.primaryIndexMap[key];
            this.deleted++;
            return true;
        }
        return false;
    }
    removeAll(items) {
        if (!items || items.length === 0)
            return 0;
        let ret = 0;
        items.forEach(it => {
            if (this.remove(it))
                ret++;
        });
        return ret;
    }
    clear() {
        this.deleted = 0;
    }
    containsKey(key) {
        return key && key in this.primaryIndexMap && this.primaryIndexMap[key] !== undefined;
    }
    contains(item) {
        const key = this.getKey(item);
        return key ? this.primaryIndexMap[key] !== undefined : false;
    }
    size() {
        return this.array.length - this.deleted;
    }
    update(item) {
        const key = this.getKey(item);
        if (key) {
            const idx = this.primaryIndexMap[key];
            if (this.indexAcceptable(idx)) {
                this.array[idx] = item;
                return true;
            }
        }
        return false;
    }
    getAll(cnt, str) {
        let count = cnt;
        let start = str;
        if (count !== undefined) {
            if (start === undefined) {
                start = 0;
            }
            if (count <= 0) {
                count = 1;
            }
            else {
                count = count > this.size() ? this.size() : count;
            }
            return this.array.slice(start, start + count);
        }
        return this.array;
    }
}
export class FixSizeQueue {
    constructor(size) {
        if (size && size <= 1)
            throw new Error('size illegal');
        this.maxSize = size || 16;
        this.array = new Array(this.maxSize);
    }
    [Symbol.iterator]() {
        return new ArrayIterator(this.array);
    }
    push(obj) {
        this.array.push(obj);
        if (this.array.length > this.maxSize) {
            this.array = this.array.slice(1, this.array.length);
        }
        return this.array.length;
    }
    pop() {
        return this.array.pop();
    }
    size() {
        return this.array.length;
    }
    max() {
        return this.maxSize;
    }
    toArray() {
        return this.array.slice();
    }
}
export class ListStore {
    constructor(initSeq, arr, capacity, sizeLimited) {
        this.capacity = 1000000;
        this.initPos = this.capacity / 2;
        this.numberRegx = /-?\d+/;
        this.index = this.initPos;
        this.forwardIndex = this.initPos - 1;
        this.total = 0;
        this.deleted = 0;
        this.forwardTotal = 0;
        this.forwardDeleted = 0;
        if (capacity)
            this.capacity = capacity;
        this.limitSize = !!sizeLimited;
        if (initSeq) {
            this.initPos = initSeq;
            this.index = initSeq;
            this.forwardIndex = this.initPos - 1;
        }
        this.store = new Map();
        if (arr && arr.length > 0) {
            arr.forEach(i => {
                const seq = this.index++;
                this.total++;
                this.store.set(seq, arr[Number(i)]);
            });
        }
    }
    size() {
        return this.forwardTotal + this.total - this.forwardDeleted - this.deleted;
    }
    addOb(ob, prepend) {
        let seq = 0;
        seq = prepend ? this.forwardIndex-- : this.index++;
        if (this.limitSize && (seq < 0 || seq > this.capacity)) {
            throw new Error('not enough room');
        }
        if (prepend) {
            this.forwardTotal++;
        }
        else {
            this.total++;
        }
        this.store.set(seq, ob);
        return seq;
    }
    getOb(id) {
        return this.store.get(id);
    }
    removeOb(id) {
        let ret;
        if (this.store.get(id)) {
            ret = this.store.get(id);
            this.store.delete(id);
        }
        if (id >= this.initPos) {
            this.deleted++;
        }
        else {
            this.forwardDeleted++;
        }
        return ret;
    }
    iterate(iterator) {
        return new Promise((r, j) => {
            if (this.total === 0) {
                j(new Error('no ob registered'));
            }
            else {
                let total = 0;
                this.store.forEach(it => {
                    if (it) {
                        try {
                            iterator(it);
                            ++total;
                        }
                        catch (e) {
                            console.warn('[model] event failed for ' + it, e);
                        }
                    }
                });
                r(String(total));
            }
        });
    }
    rearrange(seq) {
        let recycled = 0;
        const arr = new Array(this.capacity / 2);
        if (seq < 0) {
            if (this.forwardDeleted === 0)
                return 0;
            for (let k = 0; k < this.initPos; ++k) {
                const ob = this.getOb(k);
                if (ob) {
                    arr.push(ob);
                    this.store.delete(k);
                }
            }
            this.forwardTotal = arr.length;
            this.forwardDeleted = 0;
        }
        else {
            if (this.deleted === 0)
                return 0;
            for (let k = this.capacity; k > this.initPos; --k) {
                const ob1 = this.getOb(k);
                if (ob1) {
                    arr.push(ob1);
                    this.store.delete(k);
                }
            }
            this.total = arr.length;
            this.deleted = 0;
        }
        recycled = this.capacity / 2 - arr.length;
        if (recycled <= 1)
            return 0;
        if (seq < 0) {
            this.forwardIndex = this.initPos - 1;
            arr.forEach(it => {
                this.store.set(this.forwardIndex--, it);
            });
        }
        else {
            this.index = this.initPos;
            arr.forEach(it => {
                this.store.set(this.index++, it);
            });
        }
        return recycled;
    }
    [Symbol.iterator]() {
        return new ListStoreIterator(this);
    }
}
export class ListStoreIterator {
    constructor(data) {
        this.seq = data.forwardIndex;
        this.data = data;
    }
    next() {
        do {
            const v = this.data.getOb(this.seq++);
            const done = this.seq > this.data.index;
            if (v || done) {
                return {
                    done,
                    value: v,
                };
            }
        } while (this.seq <= this.data.index);
        return {
            done: true,
            value: undefined,
        };
    }
}
export class SequenceHelper {
    constructor(seq, max) {
        this.max = 10000000;
        this.seq = seq || 1;
        this.max = max || this.max;
    }
    setSeq(seq) {
        if (seq < this.max)
            this.seq = seq;
    }
    next() {
        this.seq++;
        if (this.seq > this.max) {
            this.seq = 1;
        }
        return this.seq;
    }
}
